import { campaignFeeOffer } from "../../../../components/paiement/financial";
import React, {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {Button, ConfigProvider, Form, Input, Modal, Radio, Select} from "antd";
import COUNTRY from "country-json/src/country-by-abbreviation.json";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";

import {API_URL, DEBUG} from "../../../../config";
import ApiService from "../../../../services/ApiService";
import logger from "../../../../services/logger";
import {redirectToPlatform, resolveSource} from "../../../../services/paiementLauncher";
import {getLayoutDirection} from "../../../../locales";
import PhoneInput from "../../../../components/WebEditor/components/PhoneInput";
import ExtraFields from "../../../../components/paiement/ExtraFields";
import GiftAid from "../../../../components/paiement/GiftAid";
import AACFields from "../../../../components/paiement/AACFields";
import {getIframeProtocol} from "../../../../components/paiement/iframe/protocols";
import PaymentIframe from "../../../../components/paiement/iframe/PaymentIframe";
import {countryName, realCost, taxBenefit} from "../model";
import {rememberPaiement} from "../ThankYou";
import styles from "../v2.module.css";
import {buildCheckoutConfig, checkoutRequestBody, frequency, PAIEMENT_LINK_URL} from "./payload";
import {accountPrefillWithoutDraft, restoreCheckoutValues} from "./draft";
import {assocInfoOf, platformOptions} from "./platforms";
import {sepaCheckoutKey, mandateCheckoutUrl} from "../../../../components/sepa_xml/paymentCheckout";
import StripeWalletButtons from "../../../../components/paiement/StripeWalletButtons";
import PaymentPlatformSelector from "../../../../components/paiement/PaymentPlatformSelector";
import {walletPrefill, walletMethodForPlatform, walletChoices, focusWalletField, hasWalletOptions} from "../../../../components/paiement/stripeWalletProtocol";
import {AccountAPI} from "../../../../components/AccountSpace/api";
import {buildFacturationFromAddress, buildInfoFromProfile} from "../../../../components/AccountSpace/prefillPaiement";
import {isAccountPrefillDisabled, setAccountPrefillDisabled} from "../../../../components/AccountSpace/accountPrefillToggle";
import AccountPrefillBanner from "../../../../components/WebEditor/components/AccountPrefillBanner";

// Mêmes clés que la page de paiement commune : un donateur qui a déjà donné retrouve
// ses coordonnées, quel que soit l'écran par lequel il passe.
const MEM_INFO = "MEM_INFO";
const MEM_FACTURATION = "MEM_FACTURATION";

// Pays dont le formulaire attend un état ou une province.
const STATE_FORMATS = ["US", "CA"];

const PLATFORM_LABELS = {
    CARD: () => t`Carte bancaire`,
    "STRIPE.APPLEPAY": () => t`Apple Pay`,
    "STRIPE.GOOGLEPAY": () => t`Google Pay`,
    "PRELEVEMENT.GOCARDLESS": () => t`Prélèvement bancaire`,
    "STRIPE.SEPA": () => t`Prélèvement bancaire`,
    PAYPAL: () => "PayPal",
    "CARDCOM.BIT": () => "BIT",
    "KESHER.BIT": () => "Bit",
    AAC: () => t`Achisomoch (AAC)`,
};

function readMemory(key) {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function writeMemory(key, value) {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* navigation privée : rien à mémoriser */ }
}

// Formulaire posté par le prestataire qui l'exige (jeton en champ caché).
function postToPlatform(url, token) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "token";
    input.value = token;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
}

/**
 * Écran de don de la campagne v2 : un seul écran, aux couleurs de la campagne, avec le
 * récapitulatif toujours visible.
 *
 * Il réutilise les contrats de la page de paiement commune : il construit le
 * même objet, appelle le même service et traite la réponse de la même façon —
 * redirection, formulaire posté, ou paiement dans un cadre (Nedarim Plus).
 */
export default function Checkout({context, campagneData, donation, palette, formatMoney, onBack, draftValues, onDraftChange, navigationLockedRef}) {
    const [form] = Form.useForm();
    const [walletMethods, setWalletMethods] = useState({applePay: false, googlePay: false});
    const [walletMissingFields, setWalletMissingFields] = useState([]);
    const selectedPlatform = Form.useWatch("platform", form) || "CARD";
    const walletMethod = walletMethodForPlatform(selectedPlatform);
    const walletMode = !!walletMethod;
    const showField = name => !walletMode || walletMissingFields.includes(name);

    const {org} = context;

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);
    const [iframePayment, setIframePayment] = useState(null);
    // Frais bancaires proposés au donateur avant l'envoi, comme sur la page commune.
    const [feeOffer, setFeeOffer] = useState(null);
    // Espace de compte : le donateur connecté n'a pas à ressaisir son identité, et son
    // don doit rejoindre sa fiche (comme sur la page de paiement commune).
    const [account, setAccount] = useState(null);
    const [accountDisabled, setAccountDisabled] = useState(false);
    const submittedRef = useRef(false);
    const sepaRequestRef = useRef(null);
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const setBusy = (busy) => {
        navigationLockedRef.current = busy;
        setSubmitting(busy);
    };

    useEffect(() => {
        let vivant = true;
        setAccountDisabled(isAccountPrefillDisabled());
        // Pas de garde sur un jeton : la session est un cookie non lisible en JS, seule
        // la réponse de /me tranche (403 en anonyme).
        AccountAPI.me().then(res => {
            if (!vivant || !res?.success || res?.account?.state !== "VALIDE") return;
            setAccount({
                selfContact: res.account.selfContact || null,
                profile: res.account.profile || {},
                name: [res.account.profile?.prenom, res.account.profile?.nom].filter(Boolean).join(" "),
            });
        }).catch(() => { /* silencieux : le don reste possible en anonyme */ });
        return () => { vivant = false; };
    }, []);

    // L'identité du compte l'emporte sur le souvenir du navigateur : c'est la source de
    // vérité tant que le donateur ne dit pas « ce n'est pas moi ».
    useEffect(() => {
        if (!account || accountDisabled) return;
        const adresse = Array.isArray(account.profile?.adresses) ? account.profile.adresses[0] : null;
        form.setFieldsValue(accountPrefillWithoutDraft({
            ...buildInfoFromProfile(account.profile),
            ...(adresse ? buildFacturationFromAddress(adresse) : {}),
        }, draftValues));
    }, [account, accountDisabled]);

    const changeAccountPrefill = (disabled) => {
        if (submitting) return;
        setAccountPrefillDisabled(disabled);
        setAccountDisabled(disabled);
        if (disabled) {
            // On garde les valeurs saisies : seul le rattachement au compte s'arrête.
            return;
        }
        const adresse = Array.isArray(account?.profile?.adresses) ? account.profile.adresses[0] : null;
        form.setFieldsValue({
            ...buildInfoFromProfile(account?.profile || {}),
            ...(adresse ? buildFacturationFromAddress(adresse) : {}),
        });
    };
    const accountActive = !!account && !accountDisabled;

    const assoc = donation?.campagne?.assoc || campagneData?.assoc || "MAIN";
    const assocInfo = useMemo(() => assocInfoOf(org, assoc), [org, assoc]);
    const {montant, abo, nbMonth} = frequency(donation);
    const recurrent = abo;
    // Même règle que la page commune : aucun choix trompeur quand le moyen récurrent est imposé.
    const forcedRecurrentPlatform = recurrent && org?.web?.["paiement.platform.recurrent"];

    const platforms = useMemo(
        () => platformOptions({org, assoc, assocInfo, devise: donation?.devise, recurrent}),
        [org, assoc, assocInfo, donation?.devise, recurrent]
    );

    const paymentPlatforms = hasWalletOptions(org, assoc) && !recurrent && assocInfo?.stripeAvailable === true
        ? [...platforms, ...walletChoices(walletMethods, org, assoc)] : platforms;

    const format = assocInfo?._format || null;
    const benefit = taxBenefit(assocInfo?.country || campagneData?.mainAssocInfo?.country, campagneData?.config);
    const cost = realCost(montant, benefit);

    // Liste des pays dans la langue de la page : le reste du formulaire était traduit,
    // ce menu restait en anglais, y compris en hébreu.
    const countryOptions = useMemo(() => COUNTRY
        .map(item => ({value: item.country, label: countryName(item.abbreviation, context.langValue) || item.country}))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), context.langValue || "fr")),
    [context.langValue]);

    const memory = useMemo(() => (typeof window === "undefined"
        ? {info: null, facturation: null}
        : {info: readMemory(MEM_INFO), facturation: readMemory(MEM_FACTURATION)}), []);

    const initialValues = restoreCheckoutValues({
        homme: memory.info?.homme,
        prenom: memory.info?.prenom || "",
        nom: memory.info?.nom || "",
        email: memory.info?.email || "",
        tel: memory.info?.tel || null,
        perso: memory.facturation?.perso !== false,
        adresse: memory.facturation?.adresse || "",
        postal: memory.facturation?.postal || "",
        ville: memory.facturation?.ville || "",
        // Comme la page commune : pays de l'organisation, nom anglais de la liste des pays.
        pays: memory.facturation?.pays || COUNTRY.find(item => item.abbreviation === org?.country)?.country || "France",
        etat: memory.facturation?.etat || "",
        raison: memory.facturation?.raison || "",
        siren: memory.facturation?.siren || "",
        displayName: [memory.info?.prenom, memory.info?.nom].filter(Boolean).join(" "),
        platform: platforms[0]?.value || "CARD",
    }, draftValues, platforms, abo);

    // Un lot réservé pendant que le donateur remplit le formulaire doit être rendu s'il
    // quitte la page sans payer, comme le fait la page de paiement commune.
    useEffect(() => {
        const lockId = donation?.campagne?.lockId;
        if (!lockId) return;
        const release = () => {
            if (submittedRef.current) return;
            try {
                navigator.sendBeacon?.(API_URL + "/campagne/unlock/" + lockId);
            } catch (e) { /* le navigateur part : rien de plus à tenter */ }
        };
        window.addEventListener("pagehide", release);
        return () => {
            window.removeEventListener("pagehide", release);
            release();
        };
    }, [donation?.campagne?.lockId]);

    const submit = async (values) => {
        if (walletMethodForPlatform(values.platform)) return;
        if (submitting) return;
        setBusy(true);
        setError(null);
        setDetail(null);

        writeMemory(MEM_INFO, {
            nom: values.nom, prenom: values.prenom, email: values.email, tel: values.tel, homme: values.homme,
        });

        const config = buildCheckoutConfig({
            campagneData,
            context,
            donation,
            values,
            platform: values.platform || null,
            name: t`Don pour ${campagneData?.label}`,
            source: resolveSource(),
            contact: accountActive ? account.selfContact : null,
        });
        writeMemory(MEM_FACTURATION, {...config.facturation, giftAid: undefined});

        const minFees = Number(org?.web?.suggestFees) || 0;
        const offer = campaignFeeOffer(config, donation, campagneData?.config?.monthlyAmountDisplay === true, minFees);
        if (offer) {
            setBusy(false);
            setFeeOffer(offer);
            return;
        }
        await send(config);
    };

    const chooseFees = (accept) => {
        const offer = feeOffer;
        setFeeOffer(null);
        if (!offer) return;
        setBusy(true);
        send(accept ? offer.acceptedConfig : offer.config);
    };

    const send = async (config) => {
        let res = null;
        try {
            if (config.platform === 'SEPA_XML') {
                sepaRequestRef.current = sepaCheckoutKey(config, sepaRequestRef.current);
                config.sepaXmlRequestKey = sepaRequestRef.current.key;
            }
            // Le serveur n'accepte un `contact` que s'il appartient au compte de la
            // session : le jeton ne part que par le relais authentifié.
            res = await ApiService.post(PAIEMENT_LINK_URL, checkoutRequestBody(config), {useProxy: !!config.contact});
        } catch (e) {
            if (!mountedRef.current) return;
            setBusy(false);
            setError(t`Le paiement n'a pas pu être lancé. Réessayez dans un instant.`);
            return;
        }

        // Une navigation externe peut démonter ce formulaire pendant la réponse.
        // Ne jamais lancer alors un paiement dont le verrou a déjà été libéré.
        if (!mountedRef.current) return;

        if (config.platform === 'SEPA_XML' && res?.success) {
            if (res.platform === 'SEPA_XML' && /^[a-zA-Z0-9_-]{43}$/.test(res.checkoutToken || '')) {
                submittedRef.current = true;
                redirectToPlatform(mandateCheckoutUrl(res.checkoutToken, window.location.origin, context.langValue));
                return;
            }
            res = {success: false};
        }

        if (!res || res.success === false) {
            setBusy(false);
            // Un message de la banque (souvent technique, souvent en anglais) n'a rien à
            // faire sous les yeux d'un donateur : il part au journal, pas à l'écran.
            if (res?.error) logger.warn({error: res.error}, "campagne v2 : paiement refusé");
            if (DEBUG && res?.error) setDetail(String(res.error));

            // Refus métier (dons fermés, lot épuisé, moyen de paiement impossible) : le
            // serveur envoie un message écrit pour le donateur, dans sa langue. Inviter
            // à « réessayer » serait faux : rien ne changera au prochain essai.
            if (res?.userMessage) {
                setError(String(res.userMessage));
                return;
            }

            // Lot déjà pris, réservation ou lien expirés : la page doit être rechargée,
            // pas réessayée en l'état.
            if (res?.expired) {
                setError(t`Votre réservation a expiré. Revenez à la campagne pour recommencer.`);
                return;
            }
            setError(t`Le paiement n'a pas pu être lancé. Réessayez dans un instant.`);
            return;
        }

        submittedRef.current = true;
        rememberPaiement(campagneData?.id, res.paiementId);

        // Paiement dans un cadre (Nedarim Plus) : la page reste, le prestataire s'affiche.
        const protocol = res.platform ? getIframeProtocol(res.platform) : null;
        const iframeParams = res.platform ? res[String(res.platform).toLowerCase()] : null;
        if (protocol && iframeParams) {
            setIframePayment({protocol, params: iframeParams, paiementId: res.paiementId, isSubscription: res.isSubscription});
            setSubmitting(false);
            return;
        }

        if (res.post === true && res.url) {
            postToPlatform(res.url, res.token);
            return;
        }

        if (res.url) {
            redirectToPlatform(res.url);
            return;
        }

        setBusy(false);
        setError(t`Le paiement n'a pas pu être lancé. Réessayez dans un instant.`);
    };

    // --- Paiement dans un cadre ---------------------------------------------

    if (iframePayment) {
        return (
            <section className={styles.checkout} aria-labelledby="cv-checkout-title">
                <h2 id="cv-checkout-title" className={styles.donateTitle} tabIndex={-1}><Trans>Paiement</Trans></h2>
                <PaymentIframe
                    protocol={iframePayment.protocol}
                    params={iframePayment.params}
                    onSuccess={() => {
                        const url = donation?.returnUrl;
                        if (url) window.location.href = url;
                    }}
                    onError={(message) => {
                        submittedRef.current = false;
                        navigationLockedRef.current = false;
                        setIframePayment(null);
                        setError(message || t`Le paiement n'a pas abouti.`);
                    }}
                    onCancel={() => {
                        submittedRef.current = false;
                        navigationLockedRef.current = false;
                        setIframePayment(null);
                    }}
                />
            </section>
        );
    }

    // --- Formulaire ----------------------------------------------------------

    const frequencyLabel = () => {
        // Un don mensuel à durée connue doit rappeler cette durée ici aussi : l'écran
        // précédent annonçait « pendant 12 mois », celui-ci n'en disait plus rien.
        if (donation?.monthly) {
            return nbMonth > 1 ? t`Chaque mois pendant ${nbMonth} mois` : t`Chaque mois`;
        }
        if (donation?.months > 1) return t`${formatMoney(montant, donation.devise)} par mois pendant ${donation.months} mois`;
        return t`En une fois`;
    };

    return (
        <ConfigProvider
            direction={getLayoutDirection(context.langValue)}
            theme={{token: {colorPrimary: palette.accent, borderRadius: 12, fontFamily: "inherit", controlHeight: 48, fontSize: 16}}}
        >
            <section className={styles.checkout} aria-labelledby="cv-checkout-title">
                <button type="button" className={styles.backLink} onClick={onBack} disabled={submitting}>
                    <Trans>Modifier mon don</Trans>
                </button>

                <h2 id="cv-checkout-title" className={styles.donateTitle} tabIndex={-1}><Trans>Finaliser mon don</Trans></h2>

                {/* Récapitulatif toujours visible : ce qui sera débité, et par qui. */}
                <div className={styles.summary}>
                    <div className={styles.summaryRow}>
                        <span>{campagneData?.config?.title || campagneData?.label}</span>
                        <strong>{formatMoney(donation.amount, donation.devise)}</strong>
                    </div>
                    <div className={styles.summaryMeta}>{frequencyLabel()}</div>
                    {cost !== null && benefit?.kind === "REDUCTION" && (
                        <div className={styles.summaryMeta}>
                            <Trans>Après réduction d'impôt de 66 %, ce don vous revient à {formatMoney(cost, donation.devise)}.</Trans>
                        </div>
                    )}
                    {cost !== null && benefit?.kind === "CREDIT" && (
                        <div className={styles.summaryMeta}>
                            <Trans>Avec le crédit d'impôt de 35 % (article 46), ce don vous revient à {formatMoney(cost, donation.devise)}.</Trans>
                        </div>
                    )}
                    {assocInfo?.nom && (
                        <div className={styles.summaryMeta}>
                            <Trans>Reçu fiscal émis par {assocInfo.nom}</Trans>
                        </div>
                    )}
                </div>

                {error && (
                    <p className={styles.error} role="alert">
                        {error}
                        {detail && <span className={styles.help}> {detail}</span>}
                    </p>
                )}

                <Form noValidate form={form} disabled={submitting} layout="vertical" requiredMark={false} initialValues={initialValues}
                      onSubmitCapture={event => {
                          if (walletMethodForPlatform(form.getFieldValue("platform"))) {
                              event.preventDefault(); event.stopPropagation();
                          }
                      }}
                      onValuesChange={changes => {
                          if (Object.prototype.hasOwnProperty.call(changes, "platform")) setWalletMissingFields([]);
                          onDraftChange?.(changes);
                      }} onFinish={submit}>
                    {!forcedRecurrentPlatform && paymentPlatforms.length > 1 && (
                        <Fragment>
                            <h3 className={styles.checkoutTitle}><Trans>Moyen de paiement</Trans></h3>
                            <Form.Item name="platform">
                                <PaymentPlatformSelector options={paymentPlatforms.map(item => ({...item,
                                    label: (PLATFORM_LABELS[item.value] || (() => item.value))()}))}/>
                            </Form.Item>
                        </Fragment>
                    )}
                    {(forcedRecurrentPlatform || paymentPlatforms.length <= 1) && <Form.Item name="platform" hidden><Input/></Form.Item>}
                    <h3 className={styles.checkoutTitle}><Trans>Vos coordonnées</Trans></h3>

                    {/* Compte du donateur : mêmes libellés et même soupape que la page de
                        paiement commune, pour que les deux parcours se ressemblent. */}
                    {accountActive && (
                        <AccountPrefillBanner
                            mode="active"
                            title={account.name ? t`Connecté en tant que` + " " + account.name : t`Vous êtes connecté à votre compte`}
                            subtitle={t`Vos informations sont pré-remplies`}
                            actionLabel={t`Ce n'est pas moi`}
                            onAction={() => changeAccountPrefill(true)}
                        />
                    )}
                    {account && accountDisabled && (
                        <AccountPrefillBanner
                            mode="disabled"
                            title={t`Vous saisissez d'autres informations`}
                            subtitle={t`Ce don ne sera pas rattaché à votre compte`}
                            actionLabel={t`Réutiliser mon compte`}
                            onAction={() => changeAccountPrefill(false)}
                        />
                    )}

                    <Form.Item label={t`Civilité`} name="homme" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                        <Radio.Group disabled={accountActive || submitting}>
                            <Radio.Button value={true}><Trans>Monsieur</Trans></Radio.Button>
                            <Radio.Button value={false}><Trans>Madame</Trans></Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label={t`Prénom`} name="prenom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                        <Input autoComplete="given-name" disabled={accountActive || submitting}/>
                    </Form.Item>
                    <Form.Item label={t`Nom`} name="nom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                        <Input autoComplete="family-name" disabled={accountActive || submitting}/>
                    </Form.Item>
                    <Form.Item
                        hidden={!showField("email")}
                        label={t`Email`}
                        name="email"
                        rules={[
                            {required: true, message: t`Ce champ est obligatoire`},
                            {type: "email", message: t`Cette adresse email n'est pas valide`},
                        ]}
                        extra={t`Votre reçu fiscal y sera envoyé.`}
                    >
                        <Input type="email" autoComplete="email" disabled={accountActive || submitting}/>
                    </Form.Item>
                    <Form.Item
                        hidden={!showField("tel")}
                        label={t`Téléphone`}
                        name="tel"
                        rules={[
                            {required: true, message: t`Ce champ est obligatoire`},
                            {validator: (_, value) => (value === undefined || value === null || !Number.isNaN(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error(t`Ce numéro de téléphone n'est pas valide`)))},
                        ]}
                    >
                        <PhoneInput placeholder={t`Téléphone`} defaultLang={context.langValue} disabled={accountActive || submitting}/>
                    </Form.Item>

                    <ExtraFields org={org}/>

                    <h3 className={styles.checkoutTitle}><Trans>Votre dédicace</Trans></h3>
                    <Form.Item label={t`Nom affiché sur la page`} name="displayName" extra={t`Laissez vide pour donner anonymement.`}>
                        <Input autoComplete="off" maxLength={200}/>
                    </Form.Item>
                    <Form.Item label={t`Dédicace (facultatif)`} name="commentaire" extra={t`Elle s'affiche sous votre nom, sur la page.`}>
                        <Input.TextArea rows={3} maxLength={500} showCount/>
                    </Form.Item>

                    {!walletMode && <h3 className={styles.checkoutTitle}><Trans>Adresse du reçu fiscal</Trans></h3>}
                    <Form.Item name="perso">
                        <Radio.Group>
                            <Radio.Button value={true}><Trans>Particulier</Trans></Radio.Button>
                            <Radio.Button value={false}><Trans>Entreprise</Trans></Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.perso !== next.perso || prev.pays !== next.pays}>
                        {({getFieldValue}) => (
                            <Fragment>
                                {getFieldValue("perso") === false && (
                                    <Fragment>
                                        <Form.Item label={t`Raison sociale`} name="raison" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                            <Input/>
                                        </Form.Item>
                                        <Form.Item
                                            label={format && format !== "FR" ? t`Identifiant société` : t`Numéro SIREN`}
                                            name="siren"
                                            rules={[{required: true, message: t`Ce champ est obligatoire`}]}
                                        >
                                            <Input/>
                                        </Form.Item>
                                    </Fragment>
                                )}

                                {getFieldValue("perso") !== false && format === "IL" && (
                                    <Form.Item label={t`Numéro d'identité`} name="social_id" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                        <Input/>
                                    </Form.Item>
                                )}

                                <Form.Item hidden={!showField("adresse")} label={t`Adresse`} name="adresse" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                    <Input autoComplete="street-address"/>
                                </Form.Item>

                                {format !== "IL" && (
                                    <Form.Item hidden={!showField("postal")} label={t`Code postal`} name="postal" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                        <Input autoComplete="postal-code"/>
                                    </Form.Item>
                                )}

                                <Form.Item hidden={!showField("ville")} label={t`Ville`} name="ville" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                    <Input autoComplete="address-level2"/>
                                </Form.Item>

                                {STATE_FORMATS.includes(format) && (
                                    <Form.Item hidden={!showField("etat")} label={t`État / Province`} name="etat" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                        <Input autoComplete="address-level1"/>
                                    </Form.Item>
                                )}

                                <Form.Item hidden={!showField("pays")} label={t`Pays`} name="pays" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                    <Select
                                        showSearch
                                        options={countryOptions}
                                        filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </Form.Item>
                            </Fragment>
                        )}
                    </Form.Item>

                    {assocInfo && <GiftAid assocInfo={assocInfo}/>}

                    <Form.Item name="abo" hidden><Input/></Form.Item>

                    <AACFields/>

                    <Form.Item noStyle shouldUpdate>
                        {() => <StripeWalletButtons
                            enabled={hasWalletOptions(org, assoc) && !recurrent && assocInfo?.stripeAvailable === true}
                            selectedMethod={walletMethod}
                            onAvailability={methods => {
                                setWalletMethods(old => old.applePay === methods.applePay && old.googlePay === methods.googlePay ? old : methods);
                                const selected = walletMethodForPlatform(form.getFieldValue("platform"));
                                if (!submitting && selected && !(selected === "apple_pay" ? methods.applePay : methods.googlePay)) form.setFieldsValue({platform: "CARD"});
                            }}
                            organisation={org.id} assoc={assoc} currency={String(donation?.devise || org.devise || "eur").toLowerCase()}
                            amount={Math.round(Number(montant) * 100)} disabled={submitting} authenticated={accountActive}
                            onBusy={setBusy} onSuccess={res => {submittedRef.current = true; rememberPaiement(campagneData?.id, res.paiementId);}}
                            getIdentity={() => form.getFieldsValue(true)}
                            validateIdentity={() => form.validateFields(["homme", "prenom", "nom", ...(form.getFieldValue("perso") === false ? ["raison", "siren"] : [])])}
                            buildConfig={() => buildCheckoutConfig({campagneData, context, donation, values: form.getFieldsValue(true),
                                platform: "CARD", name: t`Don pour ${campagneData?.label}`, source: resolveSource(), contact: accountActive ? account.selfContact : null})}
                            applyDetails={details => {
                                const values = {...form.getFieldsValue(true)};
                                if (!form.isFieldTouched("pays") && !values.adresse) values.pays = "";
                                form.setFieldsValue(walletPrefill(values, details));
                            }}
                            validate={() => form.validateFields()}
                            onMissing={fields => {
                                setWalletMissingFields((fields || []).map(field => Array.isArray(field) ? field[0] : String(field).split(".").pop()));
                                const field = fields?.[0];
                                const name = Array.isArray(field) ? field : String(field || "prenom").split(".").pop();
                                requestAnimationFrame(() => {
                                    focusWalletField(form, name);
                                });
                            }}
                        />}
                    </Form.Item>
                    {!walletMode && <button type="submit" className={styles.primary} disabled={submitting}>
                        {submitting
                            ? t`Un instant…`
                            : (donation.monthly || donation.months > 1
                                ? t`Payer ${formatMoney(montant, donation.devise)} par mois`
                                : t`Payer ${formatMoney(montant, donation.devise)}`)}
                    </button>}

                    <p className={styles.help}><Trans>Paiement sécurisé. Vous pouvez encore revenir en arrière.</Trans></p>
                </Form>
            </section>

            <Modal
                open={!!feeOffer}
                closable={false}
                maskClosable={false}
                footer={null}
                title={<Trans>Souhaitez-vous annuler les frais bancaires de l'association ?</Trans>}
            >
                {feeOffer && (
                    <Fragment>
                        <p>
                            <Trans>
                                Après déduction des frais bancaires de votre don de <strong>{formatMoney(feeOffer.amount, donation.devise)}</strong>,<br />
                                l'association recevra <strong>{formatMoney(feeOffer.amount - feeOffer.fees, donation.devise)}</strong>.
                            </Trans>
                        </p>
                        <p>
                            <Trans>
                                Souhaitez-vous inclure les frais bancaires de <strong>{formatMoney(feeOffer.fees, donation.devise)}</strong> pour que l'association puisse bénéficier pleinement de votre don ?
                            </Trans>
                        </p>
                        <div style={{display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 16}}>
                            <Button size="large" onClick={() => chooseFees(false)}>
                                <Trans>Je garde le montant initial</Trans>
                            </Button>
                            <Button size="large" type="primary" onClick={() => chooseFees(true)}>
                                <Trans>J'ajoute les frais bancaires</Trans>
                            </Button>
                        </div>
                    </Fragment>
                )}
            </Modal>
        </ConfigProvider>
    );
}
